import { X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

/** `localStorage` flag gating the one-time migration notice (Resolution 19) — set on dismissal,
 * never reset, so the banner never reappears in the same browser once closed. */
const WHATS_NEW_DISMISSED_KEY = 'dgb-console:whats-new-theme-i18n-dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(WHATS_NEW_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * One-time migration notice (SL-0027, Task 12.4, Resolution 19): announces the theme/language
 * settings this StepLedger ships, causally explaining WHY the interface may already look
 * different (`theme_preference` defaults to `'system'`, Resolution 7/19) rather than only naming
 * where the new settings live. Renders nothing once dismissed for this browser.
 */
export function FirstLoadNotice() {
  const { t } = useTranslation(['shell', 'common']);
  const [dismissed, setDismissed] = useState(() => isDismissed());

  if (dismissed) {
    return null;
  }

  function handleDismiss() {
    localStorage.setItem(WHATS_NEW_DISMISSED_KEY, 'true');
    setDismissed(true);
  }

  return (
    <div className="border-border bg-accent text-accent-foreground flex items-start justify-between gap-4 border-b px-[clamp(16px,2.4vw,24px)] py-3 text-sm print:hidden">
      <p>{t('shell:whatsNew.themeAndLanguage')}</p>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('common:dismiss')}
        onClick={handleDismiss}
      >
        <X aria-hidden="true" />
      </Button>
    </div>
  );
}
